import moment from 'moment'
import { fromUTCMidnightSeconds } from 'utils/time'
import { clampStudyDay } from '../constants'
import { resolveProjectionDayCap } from '../projectionDayCap'

const resolvePersistentEndStudyDay = (
  firstStudyDay,
  schedule,
  useAbsoluteStartDate,
  absoluteStartDate,
  studyEndDay,
) => {
  const expire = schedule?.expire || {}
  let endStudyDay = resolveProjectionDayCap(studyEndDay)

  if (expire && 'never' in expire) {
    return endStudyDay
  }

  if (expire.absolute != null && useAbsoluteStartDate && absoluteStartDate) {
    try {
      const startMoment = moment.utc(absoluteStartDate).startOf('day')
      const endMoment = fromUTCMidnightSeconds(expire.absolute)
      if (startMoment.isValid() && endMoment.isValid()) {
        const absoluteEndDay = endMoment.diff(startMoment, 'days') + 1
        if (absoluteEndDay >= firstStudyDay) {
          return Math.min(endStudyDay, absoluteEndDay)
        }
      }
    } catch (_) {
      /* ignore */
    }
  }

  if (expire.chain_expire_offset != null && schedule?.deploy?.chain_deploy_offset != null) {
    const deployOffset = Number(schedule.deploy.chain_deploy_offset)
    const expireOffset = Number(expire.chain_expire_offset)
    if (Number.isFinite(deployOffset) && Number.isFinite(expireOffset)) {
      const span = Math.max(1, expireOffset - deployOffset + 1)
      return Math.min(endStudyDay, firstStudyDay + span - 1)
    }
  }

  return endStudyDay
}

const expandPersistentDays = (firstStudyDay, schedule, options) => {
  const { useAbsoluteStartDate, absoluteStartDate, studyEndDay } = options
  const start = clampStudyDay(firstStudyDay)
  const end = resolvePersistentEndStudyDay(
    start,
    schedule,
    useAbsoluteStartDate,
    absoluteStartDate,
    studyEndDay,
  )
  const days = []
  const cap = resolveProjectionDayCap(studyEndDay)
  for (let d = start; d <= end && d <= cap; d += 1) {
    days.push(d)
  }
  return days.length ? days : [start]
}

export const getRecurringOccurrenceDays = (
  firstStudyDay,
  schedule,
  useAbsoluteStartDate,
  absoluteStartDate,
  studyEndDay = null,
) => {
  const days = [clampStudyDay(firstStudyDay)]
  const recurring = schedule?.recurring

  if (recurring?.scheme === 'persistent') {
    return expandPersistentDays(firstStudyDay, schedule, {
      useAbsoluteStartDate,
      absoluteStartDate,
      studyEndDay,
    })
  }

  if (!recurring) return days

  let intervalDays = 1
  if (recurring.scheme === 'n-days' && (recurring.interval == null || recurring.interval < 1)) return days
  if (recurring.scheme === 'n-days') intervalDays = Math.floor(Number(recurring.interval)) || 1
  else if (recurring.scheme === 'n-months') {
    intervalDays = Math.max(1, Math.floor((Number(recurring.interval) || 1) * 30))
  } else if (recurring.scheme === 'n-hours') {
    intervalDays = Math.max(1, Math.floor((Number(recurring.interval) || 0) / 24))
  } else return days

  const expire = schedule?.expire || {}
  let maxOccurrences = null
  let endStudyDay = resolveProjectionDayCap(studyEndDay)

  if (recurring.recurrences != null && recurring.recurrences > 0) {
    maxOccurrences = Math.floor(Number(recurring.recurrences))
  } else if (expire.absolute != null && useAbsoluteStartDate && absoluteStartDate) {
    try {
      const startMoment = moment(absoluteStartDate)
      const endMoment = fromUTCMidnightSeconds(expire.absolute)
      if (startMoment.isValid() && endMoment.isValid()) {
        const absoluteEndDay = endMoment.diff(startMoment, 'days') + 1
        if (absoluteEndDay >= 1) endStudyDay = Math.min(endStudyDay, absoluteEndDay)
      }
    } catch (_) {
      /* ignore invalid expire.absolute */
    }
  }

  let day = firstStudyDay
  let count = 1
  while (true) {
    day += intervalDays
    if (maxOccurrences != null && count >= maxOccurrences) break
    if (day > endStudyDay) break
    days.push(clampStudyDay(day))
    count += 1
  }
  return days
}
